import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import { handleModalClose } from '../../../../utility/modal-close-handler';
import { toast } from 'react-hot-toast';
import EcdReportForm2 from '../ecd-report-form-2';
import { CONFIG } from "../../../../config";
import axios from "axios";
import FullScreenLoader from '../components/fullscreen-loader';
import { PreviousActiveContent } from '../components/previous-active-content';
const API_HOST_CRM = CONFIG.modules["crm"].host;

/**
 * A simple MUI Dialog component that accepts open and handleClose props
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls whether the dialog is open
 * @param {Function} props.handleClose - Function to call when dialog should close
 * @param {Function} props.updatePreviousActiveList - Function to call when the update is successful or deletion
 * @param {any} props.data - Data to be displayed in the dialog
 * @returns {React.ReactElement} The dialog component
 */
const PreviousActiveDialog = ({ open, handleClose, data, children, updatePreviousActiveList }) => {
  /**
   * State to handle submitting
   */
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to render key-value pairs
  const renderDataItem = (key, value, isHighlighted = false) => {
    // Skip rendering for empty arrays or objects
    if (Array.isArray(value) && value.length === 0) return null;
    if (value === null || value === undefined) return null;

    // Format the display value based on type
    let displayValue = value;
    if (typeof value === 'boolean') {
      displayValue = value ? 'Yes' : 'No';
    } else if (Array.isArray(value)) {
      displayValue = value.join(', ');
    } else if (typeof value === 'object' && value instanceof Date) {
      displayValue = formatDate(value);
    } else if (key.toLowerCase().includes('date')) {
      displayValue = formatDate(value);
    }



    return (
      <Grid item xs={6} key={key}>
        <Box sx={{ p: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {key}
          </Typography>
          <Typography
            variant={isHighlighted ? "subtitle1" : "body2"}
            fontWeight={isHighlighted ? "bold" : "regular"}
            sx={isHighlighted ? {
              color: 'primary.main',
              bgcolor: 'primary.light',
              p: 0.5,
              borderRadius: 1
            } : {}}
          >
            {displayValue.toString()}
          </Typography>
        </Box>
      </Grid>
    );
  };

  const onSaveReport = async (formData) => {
    setIsSubmitting(true);
    try {
      const fullCaseData = { ...data };
      let formDataCopy = { ...formData };
      formDataCopy.status = formDataCopy.callStatus;
      const mergedData = { ...fullCaseData, ...formDataCopy };

      const result = await axios.put(
        API_HOST_CRM + "/my-cases/active/update",
        {
          data: {
            ...mergedData,
          },
        }
      );

      if (result.data.success) {
        toast.success('Report saved successfully.');
        updatePreviousActiveList(mergedData);
        handleClose();
      }
    } catch (error) {
      toast.error('Failed to save report.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const onDeleteCase = async (caseData) => {
    setIsSubmitting(true);
    try {
      const result = await axios.delete(
        API_HOST_CRM + '/cases/delete-case',
        {
          data: {
            caseId: caseData._id
          }
        }
      );

      if (result.data.success) {
        toast.success('Case deleted successfully.');
        updatePreviousActiveList(caseData); // Pass the entire caseData object
        handleClose();
      }
    } catch (error) {
      toast.error('Failed to delete case.');
      console.error('Error deleting case:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => handleModalClose(e, reason, handleClose)}
      fullWidth
      maxWidth="md"
      aria-labelledby="previous-active-dialog-title"
    >
      <DialogTitle id="previous-active-dialog-title">
        Previous Active Case Details
      </DialogTitle>
      <DialogContent>
        <FullScreenLoader open={isSubmitting} />
        <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            {/* Highlighted fields at the top */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                {renderDataItem('Mother ID', data?.motherID, true)}
                {renderDataItem('Call Date', data?.callDate, true)}
              </Box>
              <Divider sx={{ my: 1 }} />
            </Grid>

            {/* Rest of the data */}
            {/* {data && Object.entries(data).map(([key, value]) => {
              // Skip the highlighted fields as they're already shown
              if (key === 'motherID' || key === 'callDate') return null;
              return renderDataItem(key, value);
            })} */}
            <Grid item lg={12}>
              <PreviousActiveContent caseObject={data} onDeleteCase={onDeleteCase} />
              {/* <EcdReportForm2 data={data} onSubmit={onSaveReport} /> */}
            </Grid>
          </Grid>
        </Paper>
        {children && (
          <Box sx={{ p: 1 }}>
            {children}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviousActiveDialog;
